import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  TablePagination,
  Avatar,
  LinearProgress,
  Chip
} from '@mui/material';
import {
  Search as SearchIcon,
  FileDownload as FileDownloadIcon,
  Group as GroupIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { getAllUsers } from '../../../firebase/services/userService';
import { useNotifications } from '../../../contexts/NotificationContext';
import BaseButton from '../../../components/common/BaseButton';
import exportService from '../../../services/exportService';

const MeetingsExport = () => {
  const { addSystemNotification } = useNotifications();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(null);
  const [exportResult, setExportResult] = useState(null);

  const glassBoxSx = {
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      boxShadow: '0 6px 24px rgba(0, 0, 0, 0.12)',
    }
  };

  useEffect(() => {
    const loadUsers = async () => {
      try {
        setLoading(true);
        const allUsers = await getAllUsers();
        setUsers(allUsers);
        setFilteredUsers(allUsers);
      } catch (err) {
        addSystemNotification('Erreur lors du chargement des utilisateurs:', 'error');
        setError("Impossible de charger la liste des utilisateurs.");
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredUsers(users);
      return;
    }

    const filtered = users.filter(user => {
      const searchLower = searchTerm.toLowerCase();
      const displayName = user.displayName || '';
      const email = user.email || '';

      return displayName.toLowerCase().includes(searchLower) ||
             email.toLowerCase().includes(searchLower);
    });

    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSelectUser = (userId) => {
    setSelectedUserIds(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedUserIds.length === filteredUsers.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(filteredUsers.map(user => user.id));
    }
  };

  const handleExport = async () => {
    if (selectedUserIds.length === 0) {
      addSystemNotification('Veuillez sélectionner au moins un utilisateur', 'warning');
      return;
    }

    setExporting(true);
    setExportProgress(null);
    setExportResult(null);

    try {
      const result = await exportService.exportMeetingsToZip(
        selectedUserIds,
        (progress) => setExportProgress(progress)
      );

      setExportResult(result);

      if (result.success) {
        addSystemNotification(result.message, 'success');
      } else {
        addSystemNotification(result.message, 'error');
      }
    } catch (err) {
      addSystemNotification('Erreur lors de l\'export: ' + err.message, 'error');
      setExportResult({ success: false, message: err.message });
    } finally {
      setExporting(false);
      setExportProgress(null);
    }
  };

  const getUserAvatar = (user) => {
    const name = user.displayName || user.email || 'U';
    const firstLetter = name.charAt(0).toUpperCase();

    return (
      <Avatar
        sx={{
          bgcolor: selectedUserIds.includes(user.id) ? '#4caf50' : '#9e9e9e',
          color: 'white',
          width: 40,
          height: 40,
          fontWeight: 600,
          fontSize: '1.1rem',
          transition: 'all 0.2s ease'
        }}
      >
        {firstLetter}
      </Avatar>
    );
  };

  const getProgressMessage = () => {
    if (!exportProgress) return '';

    switch (exportProgress.step) {
      case 'fetching_meetings':
        return 'Récupération des réunions...';
      case 'processing':
        return `Traitement: ${exportProgress.current}/${exportProgress.total} - ${exportProgress.meetingTitle || ''}`;
      case 'generating_zip':
        return 'Génération du fichier ZIP...';
      default:
        return 'Export en cours...';
    }
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 400,
        ...glassBoxSx,
        m: 3
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2, color: '#3F51B5' }} />
          <Typography variant="h6" sx={{ color: '#374151', fontWeight: 600 }}>
            Chargement des utilisateurs...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        sx={{
          m: 3,
          borderRadius: '12px',
          backgroundColor: 'white',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          boxShadow: '0 4px 16px rgba(239, 68, 68, 0.08)'
        }}
      >
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{
        ...glassBoxSx,
        mb: 3,
        p: 3,
        backgroundColor: '#f8fafc',
        border: '1px solid rgba(63, 81, 181, 0.1)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{
            p: 1.5,
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%)',
            boxShadow: '0 6px 20px rgba(156, 39, 176, 0.3)',
          }}>
            <FileDownloadIcon sx={{ fontSize: 28, color: 'white' }} />
          </Box>
          <Box>
            <Typography
              variant="h5"
              component="h2"
              sx={{
                fontWeight: 700,
                color: '#1f2937',
                mb: 0.5
              }}
            >
              Export des Réunions
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              Sélectionnez les utilisateurs dont vous souhaitez exporter les réunions
            </Typography>
          </Box>
        </Box>
      </Box>

      {exportResult && (
        <Alert
          severity={exportResult.success ? "success" : "error"}
          sx={{
            mb: 3,
            borderRadius: '12px',
            backgroundColor: 'white',
            border: `1px solid ${exportResult.success ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)'}`,
            boxShadow: `0 4px 16px ${exportResult.success ? 'rgba(34, 197, 94, 0.08)' : 'rgba(239, 68, 68, 0.08)'}`
          }}
          onClose={() => setExportResult(null)}
        >
          {exportResult.success ? '✅ ' : '❌ '}{exportResult.message}
        </Alert>
      )}

      {exporting && (
        <Box sx={{ ...glassBoxSx, mb: 3, p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <CircularProgress size={24} sx={{ color: '#9c27b0' }} />
            <Typography variant="body1" sx={{ fontWeight: 600, color: '#374151' }}>
              {getProgressMessage()}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={exportProgress?.progress || 0}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: 'rgba(156, 39, 176, 0.1)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                background: 'linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%)',
              }
            }}
          />
        </Box>
      )}

      <Box sx={{ ...glassBoxSx, mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            sx={{ flex: 1, minWidth: 250 }}
            variant="outlined"
            placeholder="Rechercher un utilisateur..."
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#9c27b0' }} />
                </InputAdornment>
              ),
            }}
          />
          <Chip
            icon={<GroupIcon />}
            label={`${selectedUserIds.length} utilisateur(s) sélectionné(s)`}
            sx={{
              background: selectedUserIds.length > 0
                ? 'linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%)'
                : 'rgba(0,0,0,0.08)',
              color: selectedUserIds.length > 0 ? 'white' : '#666',
              fontWeight: 600,
              px: 1
            }}
          />
          <BaseButton
            onClick={handleExport}
            disabled={selectedUserIds.length === 0 || exporting}
            startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <FileDownloadIcon />}
            sx={{
              background: 'linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%)',
              color: 'white',
              fontWeight: 600,
              px: 3,
              '&:hover': {
                background: 'linear-gradient(135deg, #7b1fa2 0%, #9c27b0 100%)',
              },
              '&.Mui-disabled': {
                background: 'rgba(0,0,0,0.12)',
                color: 'rgba(0,0,0,0.26)'
              }
            }}
          >
            {exporting ? 'Export en cours...' : 'Exporter les réunions'}
          </BaseButton>
        </Box>
      </Box>

      <Box sx={glassBoxSx}>
        <TableContainer sx={{ borderRadius: '12px', overflow: 'hidden' }}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{
                backgroundColor: '#f8fafc',
                borderBottom: '2px solid rgba(156, 39, 176, 0.1)',
              }}>
                <TableCell padding="checkbox" sx={{ width: 60 }}>
                  <Checkbox
                    indeterminate={selectedUserIds.length > 0 && selectedUserIds.length < filteredUsers.length}
                    checked={filteredUsers.length > 0 && selectedUserIds.length === filteredUsers.length}
                    onChange={handleSelectAll}
                    sx={{
                      color: '#9c27b0',
                      '&.Mui-checked': { color: '#9c27b0' },
                      '&.MuiCheckbox-indeterminate': { color: '#9c27b0' }
                    }}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151', fontSize: '0.875rem' }}>Utilisateur</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151', fontSize: '0.875rem' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151', fontSize: '0.875rem' }}>Statut</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((user) => {
                  const isSelected = selectedUserIds.includes(user.id);
                  return (
                    <TableRow
                      key={user.id}
                      hover
                      onClick={() => handleSelectUser(user.id)}
                      selected={isSelected}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#f1f5f9',
                          transition: 'all 0.2s ease'
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(156, 39, 176, 0.08)',
                          '&:hover': {
                            backgroundColor: 'rgba(156, 39, 176, 0.12)',
                          }
                        },
                        borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isSelected}
                          sx={{
                            color: '#9c27b0',
                            '&.Mui-checked': { color: '#9c27b0' }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {getUserAvatar(user)}
                          <Box>
                            <Typography variant="body1" fontWeight={600}>
                              {user.displayName || '-'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {user.id.substring(0, 8)}...
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {user.email || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {isSelected ? (
                          <Chip
                            icon={<CheckCircleIcon />}
                            label="Sélectionné"
                            size="small"
                            sx={{
                              background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                              color: 'white',
                              fontWeight: 600,
                              '& .MuiChip-icon': { color: 'white' }
                            }}
                          />
                        ) : (
                          <Chip
                            label="Non sélectionné"
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: '#9e9e9e',
                              color: '#666'
                            }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}

              {filteredUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <GroupIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        Aucun utilisateur trouvé
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Essayez de modifier votre recherche
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <Box sx={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(248,250,255,0.9) 100%)',
            backdropFilter: 'blur(10px)'
          }}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredUsers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Lignes par page"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
              sx={{
                '& .MuiTablePagination-toolbar': {
                  paddingLeft: 3,
                  paddingRight: 3,
                },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontWeight: 500,
                  color: '#9c27b0'
                }
              }}
            />
          </Box>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default MeetingsExport;
